"use client";
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Phone,
  Clock,
  Calendar,
  User,
  FileText,
  ExternalLink,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useRouter } from "next/navigation";
import { AudioPlayer } from "@/components/ui/media/audio-player";
import { useTranslations } from "next-intl"


interface CallDetails {
  id: number;
  conversation_id: string;
  external_number: string;
  agent_number: string;
  status: string;
  duration_seconds: number | null;
  created_at: string;
  voice_agent?: {
    id: string;
    name: string;
  };
  audio_url?: string;
  transcript_summary?: string;
}

interface CallDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string | null;
}

export function CallDetailsDialog({
  open,
  onOpenChange,
  conversationId,
}: CallDetailsDialogProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const tUi = useTranslations("Calls.CallsCallDetailsDialog")

  const { fetchWithAuth } = useAuth();
  const router = useRouter();
  const [call, setCall] = useState<CallDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && conversationId) {
      loadCallDetails();
    } else {
      setCall(null);
    }
  }, [open, conversationId]);

  const loadCallDetails = async () => {
    if (!conversationId) return;

    try {
      setIsLoading(true);
      const res = await fetchWithAuth(
        `/api/calls?search=${encodeURIComponent(conversationId)}&pageSize=1&source=voice_agent`
      );
      const data = await res.json();
      const callData = data.calls?.[0];

      if (callData) {
        setCall({
          id: callData.originalId,
          conversation_id: callData.voiceAgentData?.conversationId || conversationId,
          external_number: callData.callerNumber || callData.calledNumber || "Unknown",
          agent_number: callData.calledNumber || callData.callerNumber || "Unknown",
          status: callData.status,
          duration_seconds: callData.durationSeconds,
          created_at: callData.createdAt,
          voice_agent: callData.voiceAgent ? {
            id: callData.voiceAgent.id,
            name: callData.voiceAgent.name,
          } : undefined,
          audio_url: callData.voiceAgentData?.audioUrl,
          transcript_summary: callData.voiceAgentData?.transcriptSummary,
        });
      }
    } catch (error) {
      console.error("Failed to load call details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(uiFormattingLocale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    const isSuccess = status === "done" || status === "completed";
    return (
      <Badge className={isSuccess ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
        {isSuccess ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        {status}
      </Badge>
    );
  };

  const handleViewInCallLogs = () => {
    if (call?.voice_agent?.id) {
      router.push(`?page=call-logs&agentId=${call.voice_agent.id}`);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            <span><span key={tUi("callDetails")}>{tUi("callDetails")}</span></span>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : !call ? (
          <div className="text-center py-8 text-gray-500">
            <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p><span key={tUi("callNotFound")}>{tUi("callNotFound")}</span></p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Call info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1"><span key={tUi("callID")}>{tUi("callID")}</span></div>
                <div className="font-medium"><span>#</span>{call.id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1"><span key={tUi("status")}>{tUi("status")}</span></div>
                {getStatusBadge(call.status)}
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> <span><span key={tUi("externalNumber")}>{tUi("externalNumber")}</span></span>
                </div>
                <div className="font-medium">{call.external_number}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> <span><span key={tUi("duration")}>{tUi("duration")}</span></span>
                </div>
                <div className="font-medium">{formatDuration(call.duration_seconds)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <User className="w-3 h-3" /> <span><span key={tUi("agent")}>{tUi("agent")}</span></span>
                </div>
                <div className="font-medium">{call.voice_agent?.name || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> <span><span key={tUi("date")}>{tUi("date")}</span></span>
                </div>
                <div className="font-medium text-sm">{formatDate(call.created_at)}</div>
              </div>
            </div>

            {/* Audio player */}
            {call.audio_url && call.conversation_id && (
              <div className="pt-2 border-t">
                <div className="text-xs text-gray-500 mb-2"><span key={tUi("recording")}>{tUi("recording")}</span></div>
                <AudioPlayer
                  audioUrl={call.audio_url}
                  conversationId={call.conversation_id}
                  callId={call.id}
                />
              </div>
            )}

            {/* Transcript summary */}
            {call.transcript_summary && (
              <div className="pt-2 border-t">
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> <span><span key={tUi("summary")}>{tUi("summary")}</span></span>
                </div>
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {call.transcript_summary}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="pt-2 border-t flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewInCallLogs}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                <span><span key={tUi("viewInCallLogs")}>{tUi("viewInCallLogs")}</span></span>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
